import { HeaderFit, JournalService } from './JournalService';
import { JournalManifest, JournalTemplate, ListValueNote } from '../types/journal';

/** The parts of Excel's worksheet-changed event the watcher reads. */
export interface JournalSheetEdit {
  worksheetId: string;
  address: string;
  source: string;
}

export type JournalEditService = Pick<JournalService, 'manifestOf' | 'markListValues' | 'headerFit' | 'listOwner' | 'refreshListsFor' | 'recheckMarks'>;

/**
 * What the pane hears about values their lists lack: the marked values as an edit leaves them, and
 * after the lists were read again from the ERP, the values still marked with those it found there.
 */
export type JournalEditNotify = (notes: ListValueNote[], template: JournalTemplate, found?: ListValueNote[]) => void;

/** A sheet as the watcher knows it: its manifest, null for any other sheet, and how its header fits a template. */
interface KnownSheet {
  manifest: JournalManifest | null;
  fit?: { template: JournalTemplate; fit: HeaderFit };
}

/** Whether an address reaches `row` (1-based) or above it; a whole-column address does. */
function reachesRow(address: string, row: number): boolean {
  return address.split(',').some(area => {
    const rows = (area.match(/\d+/g) ?? []).map(Number);
    return rows.length === 0 || Math.min(...rows) <= row;
  });
}

/**
 * Edits on journal sheets as the user makes them: a dropdown pick becomes its code, and a value its
 * list does not have is marked at once. Works with the templates the Journal tab has loaded, and
 * leaves alone a sheet whose columns no longer line up with its template, where a mark would land
 * in the wrong column.
 */
export class JournalEditWatcher {
  private readonly sheets = new Map<string, KnownSheet>();
  /** Bumped by forget(), so a lookup that started before it is not kept. */
  private generation = 0;
  private queue: Promise<unknown> = Promise.resolve();
  /** Re-checks against the ERP run one at a time, beside the edits rather than in their queue. */
  private rechecks: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly service: JournalEditService,
    private readonly templateOf: (templateId: string) => JournalTemplate | undefined,
    private readonly notify: JournalEditNotify,
  ) {}

  forget(): void {
    this.sheets.clear();
    this.generation++;
  }

  /** A sheet just made a journal sheet, known without looking it up. */
  remember(manifest: JournalManifest): void {
    if (manifest.worksheetId) this.sheets.set(manifest.worksheetId, { manifest });
  }

  /** Queues an edit behind the ones before it, so a paste and the codes written back for it never interleave. */
  onEdit(edit: JournalSheetEdit): Promise<unknown> {
    this.queue = this.queue
      .then(() => this.handle(edit))
      .catch((error: unknown) => console.error('Failed to handle a journal sheet edit:', error));
    return this.queue;
  }

  /** Work on journal sheets that must not interleave with an edit, such as marking every cell anew. */
  inQueue<T>(work: () => Promise<T>): Promise<T> {
    const run = this.queue.then(work);
    this.queue = run.catch((error: unknown) => console.error('Failed to update a journal sheet:', error));
    return run;
  }

  /** Only this user's own edits: a co-author's add-in handles theirs. */
  async handle(edit: JournalSheetEdit): Promise<ListValueNote[]> {
    const generation = this.generation;
    if (edit.source === Excel.EventSource.remote) return [];
    const known = await this.knownSheet(edit.worksheetId);
    const manifest = known.manifest;
    if (!manifest) return [];
    const template = this.templateOf(manifest.templateId);
    if (!template) return [];
    const headerRow = manifest.headerRow ?? 1;
    if (await this.fitOf(edit, known, template, headerRow) === 'different') return [];
    const notes = await this.service.markListValues(edit.worksheetId, edit.address, template, headerRow);
    if (notes.length > 0 && generation === this.generation) {
      this.notify(notes, template);
      this.recheck(edit, template, headerRow, notes, generation);
    }
    return notes;
  }

  /**
   * A value its list lacks may have been added in the ERP since the list was read: that list is
   * read again, away from the edit queue so typing on is not held up by the ERP, and the edit looked
   * at again in the queue, so it never interleaves with an edit of the same cells. Looked at again
   * even when this re-check read nothing itself, since another may just have written the list.
   */
  private recheck(edit: JournalSheetEdit, template: JournalTemplate, headerRow: number, notes: ListValueNote[], generation: number): void {
    const owner = this.service.listOwner();
    this.rechecks = this.rechecks
      .then(() => (generation === this.generation
        ? this.service.refreshListsFor(template, { worksheetId: edit.worksheetId, headerRow }, notes, owner)
        : false))
      .catch((error: unknown) => console.error('Failed to read a journal list again from the ERP:', error))
      .then(() => {
        if (generation !== this.generation) return;
        this.queue = this.queue
          .then(async () => {
            if (generation !== this.generation) return;
            const { remaining, found } = await this.service.recheckMarks(edit.worksheetId, edit.address, template, headerRow, notes);
            if (found.length > 0) this.notify(remaining, template, found);
          })
          .catch((error: unknown) => console.error('Failed to check a journal value against the ERP:', error));
      });
  }

  private async knownSheet(worksheetId: string): Promise<KnownSheet> {
    const known = this.sheets.get(worksheetId);
    if (known) return known;
    const generation = this.generation;
    const found: KnownSheet = { manifest: await this.service.manifestOf(worksheetId) };
    if (generation === this.generation) this.sheets.set(worksheetId, found);
    return found;
  }

  /** Read once per sheet and template, and again after an edit that reaches the header row. */
  private async fitOf(edit: JournalSheetEdit, known: KnownSheet, template: JournalTemplate, headerRow: number): Promise<HeaderFit> {
    if (known.fit && known.fit.template === template && !reachesRow(edit.address, headerRow)) return known.fit.fit;
    const generation = this.generation;
    const fit = await this.service.headerFit({ worksheetId: edit.worksheetId, headerRow }, template);
    if (generation === this.generation) known.fit = { template, fit };
    return fit;
  }
}
